import React, { useEffect, useState } from "react";
import { Heart, Copy, Eye, User, MessageCircle } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { waNomor } from "./WaButtons";
import { UcapanGambar } from "./UcapanGambar";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const PRIMARY = "bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]";

const Foto = ({ j }) => (
  <Avatar className="h-12 w-12 rounded-xl shrink-0 ring-2 ring-white">
    {j?.foto_path && <AvatarImage src={fileUrl(j.foto_path)} className="object-cover" />}
    <AvatarFallback className="rounded-xl bg-rose-50 text-rose-300"><User className="h-5 w-5" /></AvatarFallback>
  </Avatar>
);

const WaKirim = ({ j, ucapan, testId }) => {
  const n = waNomor(j);
  if (!n) return <span className="text-xs text-slate-400 inline-flex items-center min-h-[36px]" data-testid={`${testId}-none`}>{(j?.nama_lengkap || "Pasangan").split(" ")[0]}: tanpa no. WA</span>;
  return <Button asChild size="sm" variant="outline" className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50 min-h-[36px]"><a href={`https://wa.me/${n}?text=${encodeURIComponent(ucapan)}`} target="_blank" rel="noreferrer" data-testid={testId}><MessageCircle className="h-4 w-4 mr-1" /> WA {(j.nama_lengkap || "").split(" ")[0]}</a></Button>;
};

// Daftar pasangan yang berulang tahun pernikahan (params sama dengan /ulang-tahun: tanggal | dari+sampai | bulan)
export const UlangTahunPernikahan = ({ params, salin }) => {
  const [data, setData] = useState({ items: [], total: 0 });
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const { page, page_size, ...p } = params;
    api.get("/ulang-tahun/pernikahan", { params: p }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [params]);
  const copy = async (it) => { const ok = await salin(it.ucapan); if (ok) toast.success("Ucapan pernikahan berhasil disalin."); else toast.error("Gagal menyalin ucapan."); };

  return (
    <section className="space-y-3" data-testid="ulang-tahun-pernikahan">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center"><Heart className="h-4 w-4" /></div>
        <div><h2 className="text-base md:text-lg font-semibold text-slate-900">Ulang Tahun Pernikahan</h2>
          <p className="text-xs text-slate-500"><span className="font-semibold" data-testid="total-pernikahan">{data.total}</span> pasangan pada periode ini</p></div>
      </div>
      {data.items.length === 0 ? (
        <Card className="p-6 text-center rounded-2xl border-dashed" data-testid="pernikahan-empty"><p className="text-sm text-slate-500">Tidak ada pasangan yang berulang tahun pernikahan pada periode ini.</p></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((it) => (
            <Card key={it.id} className="p-4 rounded-2xl border-rose-100 bg-gradient-to-br from-white to-rose-50/40 flex flex-col gap-3" data-testid={`pernikahan-card-${it.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex -space-x-3"><Foto j={it} />{it.pasangan && <Foto j={it.pasangan} />}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 break-words" data-testid={`pernikahan-nama-${it.id}`}>{it.label_pasangan}</div>
                  {!it.pasangan && !it.nama_pasangan && <div className="text-xs text-amber-700 mt-0.5">Nama pasangan belum diisi</div>}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-700">
                    <span className="inline-flex items-center gap-1"><Heart className="h-4 w-4 text-rose-500" /> {it.ulang_tahun}</span>
                    {it.tahun_ke != null && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid={`pernikahan-tahun-${it.id}`}>Tahun ke-{it.tahun_ke}</Badge>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 min-h-[44px] rounded-xl" onClick={() => setPreview(it)} data-testid={`pernikahan-preview-btn-${it.id}`}><Eye className="h-4 w-4 mr-2" /> Preview</Button>
                <Button className={`flex-1 min-h-[44px] rounded-xl ${PRIMARY}`} onClick={() => copy(it)} data-testid={`pernikahan-salin-btn-${it.id}`}><Copy className="h-4 w-4 mr-2" /> Salin Ucapan</Button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid={`pernikahan-wa-${it.id}`}>
                <WaKirim j={it} ucapan={it.ucapan} testId={`pernikahan-wa-${it.id}-a`} />
                {it.pasangan && <WaKirim j={it.pasangan} ucapan={it.ucapan} testId={`pernikahan-wa-${it.id}-b`} />}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] rounded-2xl" data-testid="pernikahan-preview-dialog">
          <DialogHeader><DialogTitle>Preview Ucapan Pernikahan</DialogTitle><DialogDescription>Nama pasangan diambil otomatis dari Data Jemaat.</DialogDescription></DialogHeader>
          {preview && <>
            <UcapanGambar meta={data.template_meta} posisi="atas" testId="pernikahan-gambar" />
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 bg-slate-50 rounded-xl p-4 border border-slate-100 max-h-[45vh] overflow-y-auto" data-testid="pernikahan-preview-text">{preview.ucapan}</pre>
            <UcapanGambar meta={data.template_meta} posisi="bawah" testId="pernikahan-gambar" />
          </>}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="min-h-[44px] rounded-xl" onClick={() => setPreview(null)}>Tutup</Button>
            <Button className={`min-h-[44px] rounded-xl ${PRIMARY}`} onClick={() => preview && copy(preview)} data-testid="pernikahan-preview-salin-btn"><Copy className="h-4 w-4 mr-2" /> Salin Ucapan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
